import { Icon } from '@iconify/react'
import { formatDate, formatDateTime, formatRelativeTime } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface DateDisplayProps {
  date: string | Date
  format?: 'date' | 'datetime' | 'relative'
  showIcon?: boolean
  className?: string
}

export function DateDisplay({
  date,
  format = 'date',
  showIcon = false,
  className,
}: DateDisplayProps) {
  const dateStr = typeof date === 'string' ? date : date.toISOString()

  const formatted = {
    date: formatDate(dateStr),
    datetime: formatDateTime(dateStr),
    relative: formatRelativeTime(dateStr),
  }[format]

  const icon = {
    date: 'solar:calendar-line-duotone',
    datetime: 'solar:clock-circle-line-duotone',
    relative: 'solar:history-line-duotone',
  }[format]

  return (
    <span className={cn('text-slate-600', className)}>
      {showIcon && (
        <Icon icon={icon} className="inline-block w-4 h-4 mr-1.5 text-slate-400" />
      )}
      {formatted}
    </span>
  )
}

// Date range for tender periods
interface DateRangeProps {
  start?: string
  end?: string
  className?: string
}

export function DateRange({ start, end, className }: DateRangeProps) {
  if (!start && !end) return <span className="text-slate-400">-</span>

  return (
    <span className={cn('text-slate-600', className)}>
      <Icon icon="solar:calendar-line-duotone" className="inline-block w-4 h-4 mr-1.5 text-slate-400" />
      {start ? formatDate(start) : '?'} - {end ? formatDate(end) : '?'}
    </span>
  )
}

// Deadline display with urgency indicator
interface DeadlineDisplayProps {
  deadline: string
  className?: string
}

export function DeadlineDisplay({ deadline, className }: DeadlineDisplayProps) {
  const deadlineDate = new Date(deadline)
  const now = new Date()
  const daysUntil = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  const getUrgencyClass = () => {
    if (daysUntil < 0) return 'text-slate-400' // Past deadline
    if (daysUntil <= 3) return 'text-rose-600' // Urgent
    if (daysUntil <= 7) return 'text-amber-600' // Warning
    return 'text-slate-600' // Normal
  }

  const getLabel = () => {
    if (daysUntil < 0) return 'Closed'
    if (daysUntil === 0) return 'Today!'
    if (daysUntil === 1) return 'Tomorrow'
    return `${daysUntil} days left`
  }

  return (
    <span className={cn('flex items-center gap-2', className)}>
      <span className="text-slate-600">
        <Icon icon="solar:alarm-line-duotone" className="inline-block w-4 h-4 mr-1 text-slate-400" />
        {formatDate(deadline)}
      </span>
      <span className={cn('text-xs font-medium px-1.5 py-0.5 rounded', getUrgencyClass())}>
        {getLabel()}
      </span>
    </span>
  )
}

// Buddhist Era year display
interface BEYearDisplayProps {
  year: number // CE year
  className?: string
}

export function BEYearDisplay({ year, className }: BEYearDisplayProps) {
  const beYear = year + 543 // Convert CE to BE

  return (
    <span className={cn('text-slate-600', className)}>
      พ.ศ. {beYear}
    </span>
  )
}
